import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    Grid,
    Hidden,
    IconButton,
    InputLabel,
    makeStyles,
    Menu,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from "@material-ui/core";
import {
    GetApp,
    Share,
    ViewColumn,
    ViewColumnOutlined,
} from "@material-ui/icons";
import { Alert } from "@material-ui/lab";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import { ChangeEvent, useContext, useEffect, useRef, useState } from "react";
import { PaymentService } from "../../services/PaymentService";
import { S3Service } from "../../services/S3Service";
import { Autocomplete } from "../Autocomplete/Autocomplete";
import { awsSettings, currencyFormatter } from "../constants";
import PrintTable from "../export/PrintTable";
import { ReportFooter } from "../settings/Header/ReportFooter";
import { ReportHeader } from "../settings/Header/ReportHeader";
import UserContext from "../UserContext";
import {
    downloadReminderPdf,
    ReminderLanguage,
    shareReminderOnWhatsapp,
} from "./paymentReminderTemplate";

const useStyles = makeStyles((theme) => ({
    feedback: {
        margin: theme.spacing(2),
    },
    noPadding: {
        padding: 0,
    },
    hoverRow: {
        "&:hover": {
            backgroundColor: "rgba(0,0,0,0.3)", // grey.300 equivalent
        },
    },

    headerStyling: {
        textTransform: "capitalize",
        "& > *": {
            backgroundColor: theme.palette.common.black,
            fontWeight: "bold",
        },
    },
}));

type TrialBalanceMode = "debit" | "credit";

const TrialBalance = () => {
    const classes = useStyles();
    const [filter, setFilter] = useState({
        search: "",
        seachForShowing: "",
        clientType: "",
        client: "",
        bank: "",
        apply: 0,
        startDate: "",
        endDate: "",
        status: "",
        city: "",
    });

    const [showColumns, setShowColumns] = useState(false);
    const [_updateSelected, setUpdateSelected] = useState<any>();
    const showColumnsBtn = useRef<HTMLButtonElement | null>(null);

    const [columnsVisibility, setColumnsVisibility] = useState<any>({
        SrNo: true,
        Client: true,
        Phone: true,
        City: true,
        balance: true,
        collection: true,
    });
    const printRef = useRef<HTMLDivElement | null>(null);

    const [printMode, setPrintMode] = useState(false);
    const [paymentToBePaid, setPaymentToBePaid] = useState<any>();
    const [count, setCount] = useState(0);
    const [pagination, setPagination] = useState({
        page: 0,
        count: 0,
        limit: 500,
    });
    const { settings, store, user } = useContext(UserContext);
    const [paymentPreferences, setPaymentPreferences] = useState({
        reminderLanguage: "english" as ReminderLanguage,
        trialBalanceMode: "debit" as TrialBalanceMode,
    });
    const [reminderLoadingId, setReminderLoadingId] = useState<number | null>(
        null
    );
    const [whatsappLoadingId, setWhatsappLoadingId] = useState<number | null>(
        null
    );
    const [clearFilters, setClearFilters] = useState(false);
    const theme = useTheme();

    const [trialBalance, setTrialBalance] = useState<any>();
    const ref = useRef(0);

    const orgName =
        settings?.["organization-name"]?.trim() ||
        (user?.baseUser?.type === "user" && user?.classroom?.class
            ? user.classroom.class
            : "");

    const resolveOrgLogoUrl = async (): Promise<string> => {
        if (store?.logo) {
            return URL.createObjectURL(store.logo);
        }

        const logoKeys = [
            settings?.["organization-logo"],
            user?.classroom?.id
                ? `${awsSettings.logosDir}/${user.classroom.id}`
                : null,
        ].filter(Boolean) as string[];

        for (const key of logoKeys) {
            try {
                const s3Url = await S3Service.getS3ByKey(key);
                if (!s3Url) continue;

                const response = await fetch(s3Url);
                if (response.ok) {
                    const blob = await response.blob();
                    return URL.createObjectURL(blob);
                }
            } catch (error) {
                // try next key
            }
        }

        return "";
    };

    const getOrgReportInfo = async () => {
        let reportsHeader: any = null;
        try {
            reportsHeader = settings?.["reports-header"]
                ? JSON.parse(settings["reports-header"])
                : null;
        } catch (error) {
            reportsHeader = null;
        }

        const logoUrl = await resolveOrgLogoUrl();

        return {
            orgContact: reportsHeader?.contactNo?.value || "",
            orgAddress: reportsHeader?.address?.value || "",
            logoUrl,
            reminderNoticeEnglish:
                reportsHeader?.reminderNoticeEnglish?.value || "",
            reminderNoticeUrdu: reportsHeader?.reminderNoticeUrdu?.value || "",
            reminderContactDetails:
                reportsHeader?.reminderContactDetails?.value || "",
        };
    };

    const handleDownloadReminderPdf = async (item: any) => {
        setReminderLoadingId(item?.id ?? null);
        try {
            const {
                orgContact,
                orgAddress,
                logoUrl,
                reminderNoticeEnglish,
                reminderNoticeUrdu,
                reminderContactDetails,
            } = await getOrgReportInfo();
            await downloadReminderPdf({
                item,
                language: paymentPreferences.reminderLanguage,
                orgName,
                orgContact,
                orgAddress,
                logoUrl,
                reminderNoticeEnglish,
                reminderNoticeUrdu,
                reminderContactDetails,
            });
        } finally {
            setReminderLoadingId(null);
        }
    };

    const handleShareReminderWhatsapp = async (item: any) => {
        setWhatsappLoadingId(item?.id ?? null);
        try {
            const {
                orgContact,
                orgAddress,
                logoUrl,
                reminderNoticeEnglish,
                reminderNoticeUrdu,
                reminderContactDetails,
            } = await getOrgReportInfo();
            await shareReminderOnWhatsapp({
                item,
                language: paymentPreferences.reminderLanguage,
                orgName,
                orgContact,
                orgAddress,
                logoUrl,
                reminderNoticeEnglish,
                reminderNoticeUrdu,
                reminderContactDetails,
            });
        } finally {
            setWhatsappLoadingId(null);
        }
    };

    const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
        ref.current++;
        const newRef = ref.current;
        const { value } = e.target;

        setFilter({ ...filter, seachForShowing: value });

        setTimeout(() => {
            if (newRef === ref.current) {
                setFilter((curr: any) => ({
                    ...curr,
                    ["search"]: value,
                }));

                ref.current = 0;
            }
        }, 1000);
    };

    const handleColumnVisibilityChange = (column: string) => {
        setColumnsVisibility((prev: any) => {
            const newVisibility = {
                ...prev,
                [column]: !prev[column],
            };

            localStorage.setItem(
                "trialBalanceRecord",
                JSON.stringify(newVisibility)
            );

            return newVisibility;
        });
    };

    const getTrialBalance = async () => {
        const [data, err] = await PaymentService.getPaymentsToBePaid(
            pagination.page + 1,
            pagination.limit,
            {
                client: filter.client ? filter.client : null,
                search: filter.search ? filter.search : null,
                city: filter.city ? filter.city : null,
                ClientType: filter.clientType ? filter.clientType : null,
                balanceType: paymentPreferences.trialBalanceMode,
            }
        );

        if (data.rows.length) {
            setTrialBalance(data.rows);
            setPagination({
                ...pagination,
                count: data?.count ?? 0,
            });
        } else {
            setPaymentToBePaid([]);
            setTrialBalance([]);
        }
    };

    const handleFilterClear = () => {
        setClearFilters(!clearFilters);
        setFilter({
            search: "",
            seachForShowing: "",
            clientType: "",
            client: "",
            bank: "",
            apply: 0,
            startDate: "",
            endDate: "",
            status: "",
            city: "",
        });

        setCount(count + 1);
    };

    useEffect(() => {
        getTrialBalance();
    }, [
        filter.apply,
        filter.search,
        count,
        pagination.page,
        pagination.limit,
        paymentPreferences.trialBalanceMode,
    ]);

    useEffect(() => {
        const savedColumns = localStorage.getItem("trialBalanceRecord");

        if (savedColumns) {
            setColumnsVisibility(JSON.parse(savedColumns));
        } else {
            setColumnsVisibility({
                SrNo: true,
                Client: true,
                Phone: true,
                City: true,
                balance: true,
                collection: true,
            });
        }
    }, []);

    useEffect(() => {
        const stored = localStorage.getItem("paymentPreferences");
        if (!stored) return;

        try {
            const parsed = JSON.parse(stored);
            setPaymentPreferences({
                reminderLanguage: parsed?.reminderLanguage ?? "english",
                trialBalanceMode:
                    parsed?.trialBalanceMode === "credit" ? "credit" : "debit",
            });
        } catch (error) {
            setPaymentPreferences({
                reminderLanguage: "english",
                trialBalanceMode: "debit",
            });
        }
    }, []);

    const handleTrialBalanceModeChange = (
        event: ChangeEvent<{ value: unknown }>
    ) => {
        const updated = {
            ...paymentPreferences,
            trialBalanceMode: event.target.value as TrialBalanceMode,
        };

        setPaymentPreferences(updated);
        localStorage.setItem("paymentPreferences", JSON.stringify(updated));
    };

    return (
        <Grid container spacing={1} style={{ padding: "1rem" }}>
            <Grid container spacing={1}>
                <Grid item xs={12} md={2}>
                    <Autocomplete
                        api="/city/get-All"
                        setOutput={(ci) =>
                            setFilter({
                                ...filter,
                                city: ci?.id || "",
                            })
                        }
                        label="City"
                        labelKey="name"
                        textFieldProps={{ variant: "outlined", size: "small" }}
                        clear={clearFilters}
                    />
                </Grid>
                <Grid item xs={12} md={2}>
                    <Autocomplete
                        labelKey="name"
                        label="Client Type"
                        api="/clientType/get-All"
                        setOutput={(r) =>
                            setFilter({
                                ...filter,
                                clientType: r?.id || null,
                            })
                        }
                        clear={clearFilters}
                        textFieldProps={{
                            variant: "outlined",
                            size: "small",
                        }}
                    />
                </Grid>

                <Grid item xs={12} md={2}>
                    <Autocomplete
                        label="Client"
                        api="/employee/all-employees"
                        labelKey="name"
                        setOutput={(opt) => {
                            setFilter({
                                ...filter,
                                client: opt?.id ?? "",
                            });
                        }}
                        textFieldProps={{ variant: "outlined", size: "small" }}
                        apiParams={{
                            clientType: filter.clientType
                                ? filter.clientType
                                : null,
                            city: filter.city ? filter.city : null,
                        }}
                        clear={clearFilters}
                    />
                </Grid>

                <Grid item xs={12} md={2}>
                    <FormControl variant="outlined" size="small" fullWidth>
                        <InputLabel id="trial-balance-mode-label">
                            Trial Balance Based On
                        </InputLabel>
                        <Select
                            labelId="trial-balance-mode-label"
                            value={paymentPreferences.trialBalanceMode}
                            onChange={handleTrialBalanceModeChange as any}
                            label="Trial Balance Based On"
                        >
                            <MenuItem value="debit">Debit</MenuItem>
                            <MenuItem value="credit">Credit</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={2}>
                    <Button
                        variant="contained"
                        fullWidth
                        color="primary"
                        onClick={() =>
                            setFilter({
                                ...filter,
                                apply: filter.apply + 1,
                            })
                        }
                        size="small"
                        style={{ height: "80%" }}
                    >
                        Apply filter
                    </Button>
                </Grid>

                <Grid item xs={3} md={2}>
                    <Tooltip title="Clear Filters">
                        <Button
                            onClick={handleFilterClear}
                            color="primary"
                            variant="outlined"
                        >
                            <ClearAllIcon /> Clear Filters
                        </Button>
                    </Tooltip>
                </Grid>
            </Grid>

            <Grid
                item
                xs={12}
                style={{
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            ></Grid>

            <Grid container style={{ justifyContent: "space-between" }}>
                <Grid
                    item
                    xs={12}
                    style={{ display: "flex", alignItems: "center" }}
                    md={4}
                >
                    {Object.keys(filter).includes("search") && (
                        <Grid item style={{ flex: "1" }}>
                            <TextField
                                fullWidth
                                placeholder="Search"
                                variant="outlined"
                                value={filter.seachForShowing}
                                onChange={handleSearch}
                                label="Search"
                                size="small"
                            />
                        </Grid>
                    )}

                    <Tooltip title="Select Columns">
                        <IconButton
                            ref={showColumnsBtn}
                            onClick={() => setShowColumns(true)}
                        >
                            {showColumns ? (
                                <ViewColumn
                                    htmlColor={theme.palette.primary.main}
                                />
                            ) : (
                                <ViewColumnOutlined />
                            )}
                        </IconButton>
                    </Tooltip>

                    <Menu
                        open={showColumns}
                        anchorEl={showColumnsBtn.current}
                        onClose={() => setShowColumns(false)}
                        elevation={4}
                        variant="menu"
                    >
                        {Object.keys(columnsVisibility).map((col) => (
                            <MenuItem key={col} style={{ paddingLeft: 0 }}>
                                <Checkbox
                                    size="small"
                                    checked={columnsVisibility[col]} // Checked if the column is visible
                                    onChange={() =>
                                        handleColumnVisibilityChange(col)
                                    }
                                    color="primary"
                                />

                                <Typography>{col}</Typography>
                            </MenuItem>
                        ))}
                    </Menu>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Box
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                        }}
                    >
                        <Hidden smDown>
                            <PrintTable
                                componentRef={printRef}
                                setPrintMode={setPrintMode}
                            />
                        </Hidden>

                        <TablePagination
                            component="div"
                            onPageChange={(ev, page) =>
                                setPagination({ ...pagination, page: page })
                            }
                            onRowsPerPageChange={(ev) =>
                                setPagination({
                                    ...pagination,
                                    limit: parseInt(ev.target.value),
                                    page: 0,
                                })
                            }
                            count={pagination.count}
                            page={pagination.page}
                            rowsPerPage={pagination.limit}
                            rowsPerPageOptions={[10, 50, 100, 200, 500]}
                        />
                    </Box>
                </Grid>
            </Grid>

            <style>
                {`
        @media print {
            body {
                font-family: Arial, sans-serif;
            }
            table {
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 20px;
             
            }
            th, td {
                border: 2px solid #ddd;
                padding: 8px;
                text-align: left;
                word-wrap: break-word;
                white-space: wrap;
            }
            th {
                background-color: #f4f4f4;
                font-weight: 900;
                text-wrap: nowrap
            }
            .report-header {
                text-align: center;
                margin-bottom: 20px;
            }
            .report-header img {
                max-width: 100px;
                margin-bottom: 10px;
            }
            .summary-row {
                font-weight: bold;
                background-color: #f9f9f9;
            }
            .exceeded-threshold {
                color: red;
                font-weight: bold;
            }
            .exceeded-threshold > span {
                display: block;
            }
            /* Apply word-wrap only for the 'description' column */
            .description-column {
                word-wrap: break-word;
                white-space: normal; /* Ensure it wraps correctly */
            }
        }
    `}
            </style>

            {trialBalance && trialBalance.length > 0 ? (
                <Grid item xs={12} ref={printRef}>
                    {printMode && (
                        <ReportHeader
                            orgInfo={(() => {
                                let reportsHeader = settings["reports-header"];
                                try {
                                    reportsHeader = JSON.parse(reportsHeader);
                                } catch (e) {
                                    reportsHeader = null;
                                }

                                return (
                                    reportsHeader || {
                                        name: {
                                            value: "Account Book",
                                            selected: true,
                                        },
                                        email: {
                                            value: "",
                                            selected: false,
                                        },
                                        contactNo: {
                                            value: "",
                                            selected: false,
                                        },
                                        address: {
                                            value: "",
                                            selected: false,
                                        },
                                        logoPos: {
                                            value: "left",
                                            selected: false,
                                        },
                                    }
                                );
                            })()}
                        />
                    )}
                    <TableContainer
                        style={{
                            padding: printMode ? "2rem" : "0 !important",
                            maxHeight: printMode ? undefined : "80svh", // Add scroll container height
                            overflow: printMode ? undefined : "auto", // Enable scrolling
                        }}
                    >
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow
                                    className={
                                        !printMode
                                            ? classes.headerStyling
                                            : undefined
                                    }
                                >
                                    {columnsVisibility.SrNo && (
                                        <TableCell>SR No</TableCell>
                                    )}
                                    {columnsVisibility.Client && (
                                        <TableCell>Client Name</TableCell>
                                    )}

                                    {columnsVisibility.Phone && (
                                        <TableCell>Phone</TableCell>
                                    )}

                                    {columnsVisibility.City && (
                                        <TableCell>City</TableCell>
                                    )}
                                    {columnsVisibility.balance && (
                                        <TableCell>Balance</TableCell>
                                    )}
                                    {columnsVisibility.collection && (
                                        <TableCell>Collection</TableCell>
                                    )}
                                    <TableCell>Reminder</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {trialBalance?.map((item: any, idx: number) => (
                                    <TableRow className={classes.hoverRow}>
                                        {columnsVisibility.SrNo && (
                                            <TableCell>{idx + 1}</TableCell>
                                        )}

                                        {columnsVisibility.Client && (
                                            <TableCell>
                                                {item.client.employee.name}
                                            </TableCell>
                                        )}

                                        {columnsVisibility.Phone && (
                                            <TableCell>
                                                {item?.client.employee
                                                    ?.phone ? (
                                                    item.client.employee.phone
                                                ) : (
                                                    <Typography
                                                        style={{
                                                            color: theme.palette
                                                                .divider,
                                                        }}
                                                    >
                                                        N/A
                                                    </Typography>
                                                )}
                                            </TableCell>
                                        )}

                                        {columnsVisibility.City && (
                                            <TableCell>
                                                {item?.client?.employee.city
                                                    .name ? (
                                                    item?.client?.employee.city
                                                        .name
                                                ) : (
                                                    <Typography
                                                        style={{
                                                            color: theme.palette
                                                                .divider,
                                                        }}
                                                    >
                                                        N/A
                                                    </Typography>
                                                )}
                                            </TableCell>
                                        )}

                                        {columnsVisibility.balance && (
                                            <TableCell>
                                                {currencyFormatter(
                                                    item.balance
                                                )}
                                            </TableCell>
                                        )}

                                        {columnsVisibility.collection && (
                                            <TableCell>
                                                ________________________________
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            <Box
                                                style={{
                                                    display: "flex",
                                                    gap: "6px",
                                                    flexWrap: "wrap",
                                                }}
                                            >
                                                <Tooltip title="Download PDF">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            disabled={
                                                                reminderLoadingId ===
                                                                item?.id
                                                            }
                                                            onClick={() =>
                                                                handleDownloadReminderPdf(
                                                                    item
                                                                )
                                                            }
                                                        >
                                                            {reminderLoadingId ===
                                                            item?.id ? (
                                                                <CircularProgress size="0.9rem" />
                                                            ) : (
                                                                <GetApp />
                                                            )}
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Share">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            disabled={
                                                                whatsappLoadingId ===
                                                                item?.id
                                                            }
                                                            onClick={() =>
                                                                handleShareReminderWhatsapp(
                                                                    item
                                                                )
                                                            }
                                                        >
                                                            {whatsappLoadingId ===
                                                            item?.id ? (
                                                                <CircularProgress
                                                                    size="0.9rem"
                                                                    color="inherit"
                                                                />
                                                            ) : (
                                                                <Share />
                                                            )}
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {printMode && <ReportFooter />}
                </Grid>
            ) : (
                <Alert severity="info" style={{ width: "100%" }}>
                    No pending payments to show trial balance
                </Alert>
            )}
        </Grid>
    );
};

export default TrialBalance;
